import React from "react";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    children: React.ReactNode;
    variant?:
    | "primary"
    | "secondary"
    | "outline"
    | "ghost"
    | "dropdown"
    | "link";
    size?: "sm" | "md" | "lg" | "xl";
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    loading?: boolean;
    fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
    children,
    variant = "primary",
    size = "md",
    leftIcon,
    rightIcon,
    loading = false,
    fullWidth = false,
    className = "",
    disabled,
    ...props
}) => {
    const baseClasses =
        "inline-flex items-center justify-center font-medium uppercase transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed";

    const variants = {
        primary:
            "bg-accentPurple text-white focus:ring-accentPurple shadow-sm",
        secondary:
            "bg-btnBg hover:bg-darkBorder text-white focus:ring-accentPurple border border-cardBorder",
        outline:
            "bg-darkBg text-white hover:text-accentPurple focus:ring-accentPurple",
        ghost: "bg-transparent text-white hover:bg-darkCard focus:ring-accentPurple",
        dropdown:
            "bg-dropdownBg hover:bg-darkBorder text-white focus:ring-accentPurple border border-dropdownBorder w-full justify-between",
        link: " bg-dropdownBg",
    };

    const sizes = {
        sm: "px-3 py-1.5 text-xs leading-[8px]",
        md: "px-5 py-2.5 text-sm text-xs leading-5 font-medium",
        lg: "px-6 py-[20px] text-sm leading-[10px]",
        xl: "px-8 py-4 text-lg",
    };

    const widthClass = fullWidth ? "w-full" : "";
    const classes = `${baseClasses} ${variants[variant]} ${sizes[size]} ${widthClass} ${className}`;

    // For outline variant, we need a special wrapper
    if (variant === "outline") {
        return (
            <div
                className={`inline-flex p-[1px] bg-gradient-to-r from-gradientFrom to-gradientTo ${widthClass} ${className}`}
            >
                <button
                    className={`${baseClasses} ${variants.outline} ${sizes[size]} w-full h-full `}
                    disabled={disabled || loading}
                    {...props}
                >
                    {!loading && leftIcon && (
                        <span className="mr-2">{leftIcon}</span>
                    )}
                    {children}
                    {!loading && rightIcon && (
                        <span className="ml-2">{rightIcon}</span>
                    )}{" "}
                </button>
            </div>
        );
    }

    return (
        <button className={classes} disabled={disabled || loading} {...props}>
            {!loading && leftIcon && <span className="mr-2">{leftIcon}</span>}
            {children}
            {!loading && rightIcon && (
                <span className="ml-2">{rightIcon}</span>
            )}{" "}
        </button>
    );
};
